import { LdInput } from "@liquid/react";
import { KeyboardEventHandler, ReactNode } from "react";
import {
  FieldValues,
  Path,
  RegisterOptions,
  ValidationValueMessage,
  useController,
  useFormContext,
} from "react-hook-form";

import { StyledDiv, StyledLabel, StyledLdLabel } from "./FormInput.style";
import { ErrorMessage } from "../ErrorMessage";

type FormInputProps<FormData extends FieldValues> = {
  name: Path<FormData>;
  label?: ReactNode;
  rules?: Exclude<
    RegisterOptions,
    "valueAsNumber" | "valueAsDate" | "setValueAs"
  >;
  disabled?: boolean;
  maxlength?: string;
  type?: string;
  max?: number;
  step?: string;
  className?: string;
  width?: number | string;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  resize?: React.ComponentProps<typeof LdInput>["resize"];
  value?: any;
  append?: any;
};

export const validateValueIsNotEmpty = (value: unknown) => {
  if (Array.isArray(value)) {
    return !!value.length;
  }
  if (typeof value === "string") {
    return !!value.trim();
  }
  return !!value;
};

function FormInput<FormData extends FieldValues>({
  name,
  label,
  rules,
  disabled,
  maxlength,
  type,
  max,
  step,
  className,
  width,
  placeholder,
  multiline,
  rows,
  resize,
  value,
  append,
}: FormInputProps<FormData>) {
  const { clearErrors, control } = useFormContext();

  const handlePreventCharacterOnTypeNumber: KeyboardEventHandler<
    HTMLLdInputElement
  > = (e) => {
    if (type === "number") {
      (e.key === "e" || e.key === "+" || e.key === "-" || e.key === ",") &&
        e.preventDefault();
    }
  };

  const requiredMessage =
    (rules?.required as ValidationValueMessage)?.message || "";

  const isRequired =
    rules?.required === true ||
    !!(rules?.required as ValidationValueMessage)?.value;

  const {
    field,
    fieldState: { invalid, error },
  } = useController({
    name,
    control,
    rules: {
      validate: (value) =>
        validateValueIsNotEmpty(value) || !isRequired || requiredMessage,
      ...rules,
    },
  });

  return (
    <StyledLdLabel className={className} width={width}>
      {label && <StyledLabel data-required={isRequired}>{label}</StyledLabel>}

      <LdInput
        {...field}
        placeholder={placeholder}
        invalid={invalid}
        name={field.name}
        value={field.value}
        disabled={disabled}
        onLdinput={(e) => {
          field.onChange(e.target.value);
          clearErrors(name);
        }}
        data-testid={`${name}-ld-input`}
        maxlength={maxlength}
        type={type}
        max={max}
        step={step}
        onKeyDown={handlePreventCharacterOnTypeNumber}
        multiline={multiline}
        rows={rows}
        resize={resize}
      >
        {append && <StyledDiv slot="end">{append.text}</StyledDiv>}
      </LdInput>

      <ErrorMessage
        testId={`${name}-input-error-message-required`}
        errorMessage={error?.message}
      />
    </StyledLdLabel>
  );
}

export { FormInput };
